import { useCallback, useContext } from "react";
import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import axios from "axios";
import { AccountContext } from "auth/accountProvider";

// Base URL of the consumer-data-processor API (same var the CPI/upload queries use).
const API_BASE = process.env.REACT_APP_LPI_API_URL;

export type MediaType = "video" | "doc";

/** Public catalog item as returned by GET /media (never includes the object key). */
export interface MediaItem {
  id: string;
  title: string;
  description: string;
  category: string;
  type: MediaType;
  filename: string;
  sizeBytes: number;
}

/** Response of GET /media/:id/url — a short-lived presigned URL. */
export interface MediaUrl {
  url: string;
  expiresAt: string;
}

interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

// Resolves the session tokens the gated /media endpoints expect. Mirrors the
// getSessionTokens helper used by the CPI/overview queries, kept local to avoid
// coupling to overviewData's private hook.
const useSessionTokens = (): (() => Promise<SessionTokens>) => {
  const { getSession } = useContext(AccountContext);
  return useCallback(async (): Promise<SessionTokens> => {
    const session = await getSession();
    if (!session) throw new Error("Session expired. Please log in again.");
    const jwtToken = session.getAccessToken?.()?.getJwtToken?.();
    const sessionId = session.getIdToken?.()?.payload?.sessionId;
    if (!jwtToken || !sessionId) {
      throw new Error("Session tokens missing. Please log in again.");
    }
    return { jwtToken, sessionId };
  }, [getSession]);
};

const authHeaders = (t: SessionTokens): Record<string, string> => ({
  Authorization: `Session ${t.jwtToken}`,
  "X-Session-Id": t.sessionId,
});

/** Keep only playable video items (webinars). Pure — exported for testing. */
export const filterWebinars = (items: MediaItem[]): MediaItem[] =>
  items.filter((i) => i.type === "video");

/** Keep only document items (downloadable templates). Pure — for testing. */
export const filterDocs = (items: MediaItem[]): MediaItem[] =>
  items.filter((i) => i.type === "doc");

async function fetchAllMedia(
  auth: SessionTokens,
  signal?: AbortSignal
): Promise<MediaItem[]> {
  const res = await axios.get<{ items: MediaItem[] }>(`${API_BASE}/media`, {
    headers: authHeaders(auth),
    signal,
  });
  return res.data.items ?? [];
}

/**
 * The webinar catalog: GET /media, narrowed to video items. Uses the shared
 * QueryClient defaults (staleTime Infinity), so it fetches once per session.
 */
export const useWebinars = (): UseQueryResult<MediaItem[]> => {
  const getAuth = useSessionTokens();
  return useQuery({
    queryKey: ["media", "webinars"] as const,
    queryFn: async ({ signal }): Promise<MediaItem[]> =>
      filterWebinars(await fetchAllMedia(await getAuth(), signal)),
  });
};

/** Downloadable templates: GET /media, narrowed to doc items. */
export const useDocs = (): UseQueryResult<MediaItem[]> => {
  const getAuth = useSessionTokens();
  return useQuery({
    queryKey: ["media", "docs"] as const,
    queryFn: async ({ signal }): Promise<MediaItem[]> =>
      filterDocs(await fetchAllMedia(await getAuth(), signal)),
  });
};

/**
 * Returns a `download(id)` that fetches a doc's presigned URL and triggers a
 * browser download. The backend sets Content-Disposition: attachment on doc
 * URLs, so this saves the file rather than opening it inline.
 */
export const useTemplateDownload = (): ((id: string) => Promise<void>) => {
  const getAuth = useSessionTokens();
  return useCallback(async (id: string): Promise<void> => {
    const auth = await getAuth();
    const res = await axios.get<MediaUrl>(`${API_BASE}/media/${id}/url`, {
      headers: authHeaders(auth),
    });
    const a = document.createElement("a");
    a.href = res.data.url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [getAuth]);
};

/** The full media catalog (all types) — used by the admin portal. */
export const useMediaAssets = (): UseQueryResult<MediaItem[]> => {
  const getAuth = useSessionTokens();
  return useQuery({
    queryKey: ["media", "all"] as const,
    queryFn: async ({ signal }): Promise<MediaItem[]> =>
      fetchAllMedia(await getAuth(), signal),
  });
};

/**
 * A short-lived presigned URL for one webinar, fetched on demand (when the
 * player modal opens for `id`). The URL expires (2h for videos), so this query
 * never caches: staleTime/cacheTime 0, and it only runs while `id` is set.
 */
export const useWebinarUrl = (id: string | null): UseQueryResult<MediaUrl> => {
  const getAuth = useSessionTokens();
  return useQuery({
    queryKey: ["media", "url", id] as const,
    enabled: Boolean(id),
    staleTime: 0,
    cacheTime: 0,
    queryFn: async ({ signal }): Promise<MediaUrl> => {
      const auth = await getAuth();
      const res = await axios.get<MediaUrl>(`${API_BASE}/media/${id}/url`, {
        headers: authHeaders(auth),
        signal,
      });
      return res.data;
    },
  });
};

// --- Admin (super_admin) -----------------------------------------------------

interface UploadUrlResponse {
  putUrl: string;
  key: string;
}

export interface NewAssetInput {
  file: File;
  title: string;
  description: string;
  type: MediaType;
}

/** Admin operations. Returned functions invalidate the media cache on success. */
export const useMediaAdmin = () => {
  const getAuth = useSessionTokens();
  const qc = useQueryClient();

  /**
   * Full upload flow: mint a presigned PUT, upload the file directly to Spaces
   * (reporting progress 0-100), then record it as a catalog asset.
   */
  const upload = useCallback(
    async (
      input: NewAssetInput,
      onProgress?: (percent: number) => void
    ): Promise<MediaItem> => {
      const auth = await getAuth();

      const { data: presign } = await axios.post<UploadUrlResponse>(
        `${API_BASE}/media/admin/upload-url`,
        { filename: input.file.name, type: input.type },
        { headers: authHeaders(auth) }
      );

      await axios.put(presign.putUrl, input.file, {
        headers: {
          "Content-Type": input.file.type || "application/octet-stream",
        },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      });

      const category = input.type === "video" ? "Webinars" : "Docs";
      const { data: asset } = await axios.post<MediaItem>(
        `${API_BASE}/media/admin`,
        {
          title: input.title,
          description: input.description,
          category,
          type: input.type,
          key: presign.key,
          filename: input.file.name,
          sizeBytes: input.file.size,
        },
        { headers: authHeaders(auth) }
      );

      await qc.invalidateQueries({ queryKey: ["media"] });
      return asset;
    },
    [getAuth, qc]
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      const auth = await getAuth();
      await axios.delete(`${API_BASE}/media/admin/${id}`, {
        headers: authHeaders(auth),
      });
      await qc.invalidateQueries({ queryKey: ["media"] });
    },
    [getAuth, qc]
  );

  return { upload, remove };
};
